import { hydrateRoot, createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';

Sentry.init({
  dsn: 'https://40789f0d08277e9374694dee6c2477a1@o4507145973661696.ingest.de.sentry.io/4507145977397328',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,

  // Filter out errors we can't control
  // IMPORTANT: Wrapped in try-catch to prevent infinite loops
  beforeSend(event, hint) {
    try {
      const error = hint.originalException;

      // Safely extract error message
      let errorMessage = '';
      try {
        errorMessage = typeof error === 'string' ? error : error?.message || '';
      } catch (e) {
        // If getting message fails, treat as unknown error and report it
        return event;
      }

      // Get error code if available
      let errorCode = null;
      try {
        errorCode =
          typeof error === 'object' && error !== null
            ? (error as any).code
            : null;
      } catch (e) {
        // If getting code fails, continue without it
        errorCode = null;
      }

      // Filter out webkit messageHandlers errors from in-app browsers
      // These are caught by our WebKitErrorBoundary and shown with helpful message
      if (
        errorMessage.includes('webkit.messageHandlers') ||
        errorMessage.includes('window.webkit') ||
        errorMessage.includes('messageHandlers')
      ) {
        console.warn(
          'WebKit compatibility issue (handled gracefully):',
          errorMessage
        );
        return null; // Don't send to Sentry
      }

      // Filter out Facebook in-app browser errors (translation, postMessage, etc.)
      const facebookBrowserErrors = [
        'InvalidAccessError',
        'postMessage',
        'PageTranslationRequiredListener',
        'IABTranslationJS',
        'hxp-chat-suppression',
        'The object does not support the operation or argument',
      ];

      const isFacebookError = facebookBrowserErrors.some(pattern => {
        try {
          return errorMessage.includes(pattern);
        } catch (e) {
          return false;
        }
      });

      if (isFacebookError) {
        console.warn(
          'Facebook in-app browser error (not our code):',
          errorMessage
        );
        return null; // Don't send to Sentry
      }

      // Filter out crypto wallet extension errors (MetaMask, Coinbase Wallet, etc.)
      // These are from user's browser extensions, not our code
      const walletErrorPatterns = [
        'wallet must has at least one account',
        'user rejected',
        'metamask',
        'ethereum',
        'web3',
        'coinbase wallet',
        'trust wallet',
      ];

      const isWalletError =
        walletErrorPatterns.some(pattern => {
          try {
            return errorMessage.toLowerCase().includes(pattern);
          } catch (e) {
            return false;
          }
        }) ||
        errorCode === 4001 || // User rejected request (MetaMask)
        errorCode === 4100 || // Unauthorized
        errorCode === 4200 || // Unsupported method
        errorCode === 4900 || // Disconnected
        errorCode === 4901; // Chain disconnected

      if (isWalletError) {
        console.warn(
          'Crypto wallet extension error (not our code):',
          errorMessage,
          errorCode
        );
        return null; // Don't send to Sentry
      }

      return event;
    } catch (filterError) {
      // CRITICAL: If beforeSend itself throws, don't create infinite loop
      // Return the original event to ensure it gets reported
      console.error('Error in Sentry beforeSend filter:', filterError);
      return event;
    }
  },
});

// Global handler for unhandled promise rejections
// IMPORTANT: Wrap in try-catch to prevent infinite loops if error handler itself throws
window.addEventListener('unhandledrejection', event => {
  try {
    const error = event.reason;

    // Safely extract error message
    let errorMessage = '';
    try {
      errorMessage = typeof error === 'string' ? error : error?.message || '';
    } catch (e) {
      // If getting error message throws, just use empty string
      errorMessage = '';
    }

    // Safely extract error code
    let errorCode = null;
    try {
      errorCode =
        typeof error === 'object' && error !== null
          ? (error as any).code
          : null;
    } catch (e) {
      // If getting error code throws, just use null
      errorCode = null;
    }

    // Check if this is a webkit error
    if (
      errorMessage.includes('webkit.messageHandlers') ||
      errorMessage.includes('window.webkit') ||
      errorMessage.includes('messageHandlers')
    ) {
      // Prevent the default error handling
      event.preventDefault();

      // Show helpful message in console
      console.warn(
        'WebKit compatibility issue detected. If you are using an in-app browser (Facebook, Instagram, etc.), ' +
          "please open this page in your phone's regular browser (Safari/Chrome) for the best experience."
      );

      return;
    }

    // Check if this is a Facebook in-app browser error
    const facebookBrowserErrors = [
      'InvalidAccessError',
      'postMessage',
      'PageTranslationRequiredListener',
      'IABTranslationJS',
      'hxp-chat-suppression',
      'The object does not support the operation or argument',
    ];

    const isFacebookError = facebookBrowserErrors.some(pattern => {
      try {
        return errorMessage.includes(pattern);
      } catch (e) {
        return false;
      }
    });

    if (isFacebookError) {
      // Prevent the default error handling
      event.preventDefault();

      // Log quietly - this is from Facebook's injected code
      console.debug(
        'Facebook in-app browser error (suppressed):',
        errorMessage
      );
      return;
    }

    // Check if this is a crypto wallet extension error
    const walletErrorPatterns = [
      'wallet must has at least one account',
      'user rejected',
      'metamask',
      'ethereum',
      'web3',
      'coinbase wallet',
      'trust wallet',
    ];

    const isWalletError =
      walletErrorPatterns.some(pattern => {
        try {
          return errorMessage.toLowerCase().includes(pattern);
        } catch (e) {
          return false;
        }
      }) ||
      errorCode === 4001 || // User rejected request (MetaMask)
      errorCode === 4100 || // Unauthorized
      errorCode === 4200 || // Unsupported method
      errorCode === 4900 || // Disconnected
      errorCode === 4901; // Chain disconnected

    if (isWalletError) {
      // Prevent the default error handling
      event.preventDefault();

      // Log quietly - this is from user's browser extension, not our app
      console.debug(
        'Crypto wallet extension error (suppressed):',
        errorMessage,
        errorCode
      );
      return;
    }
  } catch (handlerError) {
    // CRITICAL: If the error handler itself throws, prevent infinite loop
    // by catching it and NOT rethrowing
    console.error(
      'Error in unhandledrejection handler (preventing infinite loop):',
      handlerError
    );
    // Do NOT rethrow or call event.preventDefault() - let the error bubble normally
  }
});

const container = document.getElementById('root')!;

// Check if the app was server-rendered by looking for SSR marker
if (container.innerHTML && container.innerHTML.trim() !== '') {
  hydrateRoot(container, <App />);
} else {
  createRoot(container).render(<App />);
}
